import { inject } from '@angular/core';
import type { ResolveFn } from '@angular/router';
import { SubscriptionService } from '@app/+authenticated/+reports/shared/subscriptions/subscription.service';
import { AccountService } from '@app/reducers/account/account.service';
import { featureFlagContextReady, FeatureService } from '@sb/shared/feature';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

/**
 * Resolves the Unleash user context for the authenticated routes `AuthenticatedGuard`
 * never reaches: it redirects to the `+errors` pages before it runs its own gate, so
 * without this they evaluate feature flags against the anonymous context the client boots
 * with.
 *
 * The context needs the account and its subscription in the store, and those pages skip
 * `DataStartup.requiredLoader()` entirely, so the loads happen here. A failed load leaves
 * the context unresolved on purpose — the wait is bounded, and evaluating against the
 * anonymous context beats not rendering.
 *
 * A resolver rather than a guard: the router runs a route's guards concurrently, so a
 * guard here would race the sibling guard's auth check and send these requests before the
 * user is in the store — they would carry no `X-App-User` header, come back 401, and the
 * unauthorized interceptor would log the user out. Resolvers run only after every guard
 * has passed, so the user is authenticated by the time this runs.
 */
export const featureFlagContextResolver: ResolveFn<boolean> = () => {
  const featureService = inject(FeatureService);
  const accountService = inject(AccountService);
  const subscriptionService = inject(SubscriptionService);

  return forkJoin([
    accountService.load().pipe(catchError(() => of(null))),
    subscriptionService.getSubscription().pipe(catchError(() => of(null))),
  ]).pipe(switchMap(() => featureFlagContextReady(featureService)));
};
